import { useCallback, useMemo } from 'react'
import { useLocation, useParams } from 'wouter'
import { skipToken, useSuspenseQuery } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'

import { PromotionForm } from '@/features/promotion'
import { DialogHeader } from '@/layouts/Dialog'
import { SelectGoodie } from '@/features/goodies'
import { Suspense } from '@/components/ui/Suspense'
import { useCloseModal } from '@/Router'
import { Permission, useCheckPermission } from '@/features/auth'

function CreatePromotion({ goodieToken }: { goodieToken?: string }) {
  const { t } = useLingui()
  const readOnly = !useCheckPermission([Permission.PROMOTION_CREATE])

  const [, navigate] = useLocation()
  const closeModal = useCloseModal()

  const { data } = useSuspenseQuery(
    AdminAPI.goodie.GET_BY_TOKEN,
    goodieToken
      ? {
          variables: { token: goodieToken }
        }
      : skipToken
  )

  const goodie = useMemo(
    () => getFragment(SharedAPI.goodie.GOODIE_FRAGMENT, data?.getGoodieByToken),
    [data]
  )

  const onBack = useCallback(() => {
    navigate('/create-promotion')
  }, [navigate])

  const onSelectGoodie = useCallback(
    (goodie: GraphQL.GoodieFragmentFragment) => {
      navigate(`/create-promotion/${goodie.token}`)
    },
    [navigate]
  )

  const onDone = useCallback(
    (promotion?: GraphQL.PromotionFragmentFragment) => {
      if (!promotion) {
        onBack()

        return
      }

      closeModal()
    },
    [onBack, closeModal]
  )

  if (!goodie) {
    return (
      <div className="flex flex-col gap-2 p-5">
        <DialogHeader
          title={t`Choose Goodie`}
          description={t`You need to choose a Goodie to create a promotion from.`}
        />
        <SelectGoodie onSelectedGoodie={onSelectGoodie} />
      </div>
    )
  }

  const goodieTitle = goodie.title

  return (
    <div className="flex flex-col gap-2 p-5">
      <DialogHeader
        title={t`Create Promotion`}
        description={t`Create a new promotion based on the "${goodieTitle}" Goodie`}
      />
      <div className="flex flex-row justify-start pl-2 pb-2">
        <button onClick={onBack} className="text-info-500" tabIndex={0}>
          <Trans comment="Label of a button to choose another Goodie">
            Choose another Goodie
          </Trans>
        </button>
      </div>
      <PromotionForm goodieId={goodie.id} onDone={onDone} readOnly={readOnly} />
    </div>
  )
}

export default function CreatePromotionSuspense() {
  const params = useParams() as { goodieToken?: string }

  if (!params) return

  return (
    <Suspense isDialog>
      <CreatePromotion goodieToken={params.goodieToken} />
    </Suspense>
  )
}
